import React, { Dispatch, SetStateAction, useCallback } from 'react';

import classNames from 'classnames';
import {
  ArrowBackRounded as ArrowBackRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
} from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';

import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Button } from '../../Button';
import { ButtonGroup } from '../../ButtonGroup';
import { Portal } from '../../Portal';

import { useStyles } from './PreviewButtons.styles';

interface PreviewButtonsProps {
  pagesCount: number;
  nextPageNumber: number;
  pageNumber: number;
  scale: number;
  zoomLevels: number[];
  handleZoom: (scale: number) => void;
  handleNextPage: Dispatch<SetStateAction<number>>;
  onClose: () => void;
  withBottomOffset?: boolean;
}

export const PreviewButtons = ({
  handleNextPage,
  pagesCount,
  nextPageNumber,
  pageNumber,
  scale,
  zoomLevels,
  handleZoom,
  onClose,
  withBottomOffset,
}: PreviewButtonsProps) => {
  const { classes } = useStyles();

  const options = zoomLevels.map((zoomLevel) => ({
    label: `${zoomLevel * 100}%`,
    value: zoomLevel,
  }));

  const zoomIn = () => {
    const nextZoom = zoomLevels.find((level) => level > scale);

    if (nextZoom) {
      handleZoom(nextZoom);
    }
  };

  const zoomOut = () => {
    const prevZoom = [...zoomLevels].reverse().find((level) => level < scale);

    if (prevZoom) {
      handleZoom(prevZoom);
    }
  };

  const handlePrev = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleNextPage((x: number) => Math.max(1, x - 1));
    },
    [handleNextPage],
  );

  const handleNext = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleNextPage((x: number) => Math.min(pagesCount ?? 0, x + 1));
    },
    [handleNextPage, pagesCount],
  );

  const getIconColor = (isDisabled: boolean) =>
    isDisabled ? 'disabled' : 'inherit';

  const getIconColorForNewIcon = (isDisabled: boolean) =>
    isDisabled ? 'stroke.secondary' : 'bg.light.primary';

  return (
    <Portal>
      <div
        className={classNames(classes.buttonGroup, {
          [classes.bottomOffset]: withBottomOffset,
        })}
        data-testid="preview-buttons"
      >
        <ButtonGroup
          orientation="horizontal"
          variant="contained"
          size="small"
          onClick={(event) => event.stopPropagation()}
          className={classNames(classes.rootBox, {
            [classes.hiddenGroup]: pagesCount <= 1,
          })}
        >
          <Button
            onClick={handlePrev}
            icon={
              <ArrowBackRoundedIcon color={getIconColor(nextPageNumber <= 1)} />
            }
          />
          <Button disabled>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={18}
            >
              {pageNumber}/{pagesCount}
            </Box>
          </Button>
          <Button
            onClick={handleNext}
            icon={
              <ArrowForwardRoundedIcon
                color={getIconColor(nextPageNumber >= pagesCount)}
              />
            }
          />
          <Button
            icon={
              <Icon
                name="magnifier_minus_outline"
                size="20"
                color={getIconColorForNewIcon(scale === zoomLevels[0])}
              />
            }
            onClick={zoomOut}
          />
          <Autocomplete
            options={options}
            disableClearable
            getOptionLabel={(option) => option.label}
            classes={{
              root: classes.root,
              option: classes.option,
              inputRoot: classes.input,
              popper: classes.popper,
              paper: classes.paper,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    root: classes.inputRoot,
                  },
                }}
                sx={{
                  '& .MuiInput-underline:before': {
                    borderBottom: 'none',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none',
                  },
                }}
              />
            )}
            popupIcon={
              <Icon
                name="dropdown_outline"
                color="original.brand.secondary"
                size="12"
              />
            }
            value={options.find((option) => option.value === scale)}
            onChange={(_, newValue) => handleZoom(newValue.value)}
          />
          <Button
            icon={
              <Icon
                name="magnifier_plus_outline"
                size="20"
                color={getIconColorForNewIcon(
                  scale === zoomLevels[zoomLevels.length - 1],
                )}
              />
            }
            onClick={zoomIn}
          />
          <Button
            icon={<Icon name="cross_large_outline" size="14" />}
            onClick={onClose}
          />
        </ButtonGroup>
      </div>
    </Portal>
  );
};
